import Banner from '@/gf/components/Banner'
import Button from '@/gf/components/ButtonOld'
import Card from '@/gf/components/Card'
import Layout from '@/gf/components/Layout'
import Link from '@/gf/components/LinkOld'
import ModalForm from '@/gf/components/ModalForm'
import Page from '@/gf/components/Page'
import ReportingResponsiveLineChart from '@/gf/components/ReportingResponsiveLineChart'
import Select from '@/gf/components/Select'
import Spinner from '@/gf/components/Spinner'
import useConfig from '@/gf/hooks/useConfig'
import useMsgs from '@/gf/hooks/useMsgs'
import useToggle from '@/gf/hooks/useToggle'
import FilterSet from '@/gf/modules/FilterSet'
import FormatFilters from '@/gf/modules/FormatFilters'
import MetricChart from '@/gf/modules/MetricChart'
import ReportingContext from '@/gf/modules/Reporting'
import Time from '@/gf/modules/Time'
import UserContext from '@/gf/modules/User'
import DateTimeInRangeFilter from '@/gf/modules/filters/DateTimeInRange'
import * as api from '@/suppliers/modules/api'
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Store } from '../../types'
import Frame from '../components/Frame'
import UpdateProfile from '../components/UpdateProfile'
import useOrderReportingMetrics from '../hooks/useOrderReportingMetrics'
import useSendEnableGearflowPaymentsEmail from '../hooks/useSendEnableGearflowPaymentsEmail'
import useSession from '../hooks/useSession'
import QueryString from '../modules/QueryString'

const timezone = Time.localTimezone()

const Home = () => {
  const { user, store } = useSession()
  const [_msgs, msgsMgr] = useMsgs()
  const { buyersUrl } = useConfig()
  const navigate = useNavigate()
  const [openOrdersCount, setOpenOrdersCount] = useState<number>()
  const [activeListingsCount, setActiveListingsCount] = useState<number>()
  const [activeStore, setActiveStore] = useState<Store>()
  const [spinnerLive, spinnerToggler] = useToggle()
  const [enableGearflowPaymentsModalOpen, enableGearflowPaymentsModalToggler] = useToggle()
  const [sendEnableGearflowPaymentsEmail] = useSendEnableGearflowPaymentsEmail()
  const [profileModalOpen, profileModalToggler] = useToggle()

  React.useEffect(() => {
    if (user && !user.phoneNumber) {
      profileModalToggler.on()
    }
  }, [user])

  const [selectedPartsSpendChartRangeId, setSelectedPartsSpendChartRangeId] = useState<string>(
    MetricChart.initChartRangeOption.id
  )
  const selectedPartsSpendChartRange = MetricChart.selectedChartRangeOption(
    selectedPartsSpendChartRangeId
  )

  const {
    data: orderReportingData,
    loading: orderReportingDataLoading,
    error: orderReportingDataError,
  } = useOrderReportingMetrics({
    filters: FormatFilters.andFilters(
      FilterSet.toApiFilters([
        DateTimeInRangeFilter.build({
          fieldId: 'inserted_at',
          from: selectedPartsSpendChartRange.fromDate.toISOString(),
          to: selectedPartsSpendChartRange.toDate.toISOString(),
        }),
      ])
    ),
    timezone,
  })

  // Get the open orders count
  const defaultOpenOrdersFilters = [
    ['not_equals', 'state', 'shipped'],
    ['not_equals', 'state', 'ready_for_pickup'],
    ['not_equals', 'state', 'cancelled'],
    ['not_equals', 'state', 'refunded'],
  ]
  const openOrdersFilters = UserContext.isAdmin(user)
    ? // Admin
      FormatFilters.andFilters(defaultOpenOrdersFilters)
    : // Not Admin
      store.onboardedSuppDashDatetime
      ? FormatFilters.andFilters([
          ['equals', 'storeId', store.id],
          ['greater_than', 'inserted_at', store.onboardedSuppDashDatetime],
          ...defaultOpenOrdersFilters,
        ])
      : FormatFilters.andFilters([['equals', 'storeId', store.id], ...defaultOpenOrdersFilters])

  if (!UserContext.isAdmin(user)) {
    React.useEffect(() => {
      api
        .searchStoreOrders({ filters: openOrdersFilters })
        .then((storeOrdersSearch) => setOpenOrdersCount(storeOrdersSearch.pagination.totalResults))

      api.fetchStore(store.id).then(setActiveStore)
    }, [])
  }

  // Get the active listings count
  if (!UserContext.isAdmin(user)) {
    React.useEffect(() => {
      api
        .searchProducts({
          filters: [['and'], ['equals', 'storeId', store.id], ['equals', 'enabled', 'true']],
        })
        .then((productsSearch) => setActiveListingsCount(productsSearch.pagination.totalResults))
    }, [])
  }

  return (
    <Frame>
      <UpdateProfile user={user} open={profileModalOpen} onClose={profileModalToggler.off} />
      <Page
        title={`${store?.name}${UserContext.isAdmin(user) ? ' (admin)' : ''}`}
        titleRightContent={
          <div className="flex flex-horizontal justify-end gap-x-2 text-sm text-gray-500">
            <>
              {store?.showPartsHub && (
                <a
                  href={buyersUrl}
                  className="block border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 text-gray-700 border-gray-300 bg-white hover:bg-gray-50 focus:ring-yellow-500 px-4 py-2 text-sm"
                >
                  Parts Hub
                </a>
              )}
            </>
          </div>
        }
      >
        <p className="text-med mt-3 mb-5 text-gray-800">
          Welcome, {user.name}.{' '}
          {activeStore && !activeStore.address && 'There are a few items for you to complete.'}
        </p>
        <Layout>
          {activeStore && !activeStore.address && (
            <Layout.Section type="full">
              <Card title="Add an address to your store">
                <Card.Section>
                  <p className="mb-6 text-gray-700">
                    Your store is missing an address. Without one, you will not be able to show
                    address information when creating purchase orders. Please add this information
                    to your profile.
                  </p>
                  <Button
                    title="Add address"
                    primary={false}
                    onClick={() => navigate(`/settings/store`)}
                  />
                </Card.Section>
              </Card>
            </Layout.Section>
          )}
          {!UserContext.isAdmin(user) &&
            !store.gearflowPaymentsEnabled &&
            !store.gearflowPaymentsRequested && (
              <>
                <Layout.Section type="full">
                  <Banner
                    text="Activate Gearflow Payments to get paid instantly."
                    buttonText="Learn more"
                    onClick={enableGearflowPaymentsModalToggler.on}
                  />
                </Layout.Section>
                <ModalForm
                  title="Activate Gearflow Payments"
                  open={enableGearflowPaymentsModalOpen}
                  onClose={enableGearflowPaymentsModalToggler.off}
                  onSubmit={(e) => {
                    e.preventDefault()
                    spinnerToggler.on()
                    sendEnableGearflowPaymentsEmail({ variables: { storeId: store.id } })
                      .then(() => {
                        msgsMgr.add(
                          'We will reach out to you shortly to activate Gearflow Payments on your account.',
                          'positive'
                        )
                      })
                      .catch(() => {
                        msgsMgr.add(
                          'Whoops, something went wrong. Please contact support.',
                          'negative'
                        )
                      })
                      .finally(() => {
                        enableGearflowPaymentsModalToggler.off()
                        spinnerToggler.off()
                      })
                  }}
                  submitButtonText="Sign up"
                >
                  {spinnerLive ? (
                    <Spinner />
                  ) : (
                    <p className="text-sm text-gray-700">
                      Gearflow Payments allows your customers to pay with credit card so you get
                      paid as soon as you mark an order as shipped. Click the button below to sign
                      up for Gearflow Payments and a Gearflow representative will be in touch to
                      activate your account.
                    </p>
                  )}
                </ModalForm>
              </>
            )}
          <Layout.Section type="half">
            <Layout.Section type="half">
              <Card
                title="Gross Part Sales"
                headerButton={
                  <Select
                    className="w-min"
                    currentId={selectedPartsSpendChartRangeId}
                    onChange={(selectedId) => setSelectedPartsSpendChartRangeId(selectedId)}
                    options={MetricChart.chartRangeOptions}
                  />
                }
              >
                <Card.Section>
                  <div className="md:h-[18.85rem] h-60 flex justify-center items-center">
                    {orderReportingDataError ? (
                      <p className="text-sm text-gray-700">
                        Failed to fetch part sales. Please contact support.
                      </p>
                    ) : orderReportingDataLoading || !orderReportingData ? (
                      <Spinner />
                    ) : (
                      <ReportingResponsiveLineChart
                        data={orderReportingData.chartData}
                        showComparison={false}
                        selectedChartTab={ReportingContext.Suppliers.tabGrossPartSales}
                        dateIntervalDays={selectedPartsSpendChartRange.intervalDays}
                      />
                    )}
                  </div>
                </Card.Section>
                <Card.Section>
                  <Link to="/reports">View Reports</Link>
                </Card.Section>
              </Card>
            </Layout.Section>
          </Layout.Section>
          <Layout.Section type="half">
            <Card title="Open Orders">
              <Card.Section>
                <div className="text-center text-6xl">{openOrdersCount ?? <Spinner />}</div>
              </Card.Section>
              <Card.Section>
                <Link to={`/orders${QueryString.openStoreOrder()}`}>View Open Orders</Link>
              </Card.Section>
            </Card>
            <Card title="Active Listings">
              <Card.Section>
                {UserContext.isAdmin(user) ? (
                  <p className="text-center text-sm text-gray-500 my-10">
                    Not implemented for admin dashboard yet
                  </p>
                ) : (
                  <div className="text-center text-6xl">{activeListingsCount ?? <Spinner />}</div>
                )}
              </Card.Section>
              <Card.Section>
                <Link to={`/products${QueryString.productEnabled(true)}`}>
                  View Active Listings
                </Link>
              </Card.Section>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    </Frame>
  )
}

export default Home
